// Tema de dados do fin — fonte ÚNICA de cor para gráfico e medidor.
//
// Antes disto havia TRÊS paletas divergentes (charts.tsx e DadosPanel.tsx com
// as mesmas 8 cores, diferindo só no primeiro slot) mais hex soltos espalhados
// em FluxoCaixa e CobrancaPanel.
//
// A paleta foi VALIDADA contra a superfície do tema neu (#e6e9f0, mais escura
// que o branco de antes) com o validador da skill `dataviz`:
//
//   node scripts/validate_palette.js "<hex,...>" --mode light --surface "#e6e9f0"
//
// O teal da marca que estava no slot 1 (#0e7c6b) REPROVA no piso de croma
// (0.094 — lê como cinza sobre a base neu). O teal-600 (#0d9488) passa em tudo
// E sobe o pior par de daltonismo de 7.9 para 12.5, melhor que a paleta do crm,
// mantendo a identidade Tupperware. Resultado:
//
//   PASS faixa de luz · PASS croma · PASS visão normal (ΔE 21.1)
//   PASS daltônicos (12.5) · WARN contraste (âmbar 2.62, lima 2.54, rosa 2.90)
//
// O WARN de contraste é OBRIGAÇÃO, não observação: exige codificação
// secundária. Regra do sistema — legenda sempre presente com 2+ séries, rótulo
// direto até 4 séries, folga de 2px entre marcas vizinhas, e visão de tabela
// disponível (que aqui existe: todo gráfico do fin tem a tabela ao lado).
//
// ANTES DE MUDAR QUALQUER COR AQUI, rode o validador de novo.

export const CHART_COLORS = [
  '#0d9488', // 1 teal   (marca)
  '#d97706', // 2 âmbar
  '#3b82f6', // 3 azul
  '#ef4444', // 4 vermelho
  '#0891b2', // 5 ciano
  '#8b5cf6', // 6 violeta
  '#65a30d', // 7 lima
  '#ec4899', // 8 rosa
] as const

// Acima de 8 séries a cor deixa de separar: dobre em "Outros", facete em small
// multiples, ou troque para composição. NUNCA gerar um 9º matiz.
export const MAX_SERIES = CHART_COLORS.length

export function seriesColor(i: number): string {
  return CHART_COLORS[i % CHART_COLORS.length]
}

// ── Séries SEMÂNTICAS do financeiro ────────────────────────────────────────
// Aqui a cor não é identidade arbitrária, carrega significado — dinheiro que
// entra, dinheiro que sai, saldo. A matiz não muda; só foi trazida para a
// paleta validada. Usar estas constantes em vez de repetir o hex.
export const MONEY = {
  in: CHART_COLORS[0],       // recebimentos, receita, entrada
  out: CHART_COLORS[3],      // pagamentos, despesa, saída
  balance: CHART_COLORS[2],  // saldo
  transfer: CHART_COLORS[5], // transferências (não afetam o resultado)
  overdue: CHART_COLORS[1],  // atraso, vencido
} as const

// Cromo do gráfico. Tokens do tema, não hex solto.
export const AXIS = 'hsl(var(--muted-foreground))'
export const GRID = 'hsl(var(--border))'
// cursor de hover: um branco translúcido some sobre a base neu.
export const CURSOR = 'rgba(30,41,59,0.06)'

// Rampa sequencial (magnitude, não identidade). Uma matiz só, do claro ao
// escuro — nunca arco-íris. É o que o ranking "Por Grupo" precisa.
export const SEQ5 = ['#dfe4ee', '#b3c4de', '#7f9cc9', '#4a74b3', '#1d4ed8'] as const

// ── Faixas de estado ────────────────────────────────────────────────────────
export type Level = 'ok' | 'warn' | 'bad' | 'idle'

export const LEVEL: Record<Level, string> = {
  ok: '#047857',
  warn: '#a16207',
  bad: '#be123c',
  idle: '#64748b',
}

export const LEVEL_TEXT: Record<Level, string> = {
  ok: 'text-emerald-600 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  bad: 'text-red-600 dark:text-red-400',
  idle: 'text-muted-foreground',
}

/** Classifica um valor em faixa. `dir` diz de que lado mora o problema. */
export function levelOf(
  value: number | null | undefined,
  warnAt: number,
  badAt: number,
  dir: 'higherIsWorse' | 'higherIsBetter' = 'higherIsWorse',
): Level {
  if (value == null || Number.isNaN(value)) return 'idle'
  if (dir === 'higherIsWorse') {
    if (value >= badAt) return 'bad'
    if (value >= warnAt) return 'warn'
    return 'ok'
  }
  if (value <= badAt) return 'bad'
  if (value <= warnAt) return 'warn'
  return 'ok'
}
